/* eslint-disable react/prop-types */

import qs from 'qs'
import React from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Account from 'common/modules/account'
import Auth from 'common/modules/auth'
import Loader from 'common/modules/loader'
import Zoid from 'common/modules/zoid'
import xFrame from 'common/xFrame'

import observers from 'apps/lib/observers'

import '@piconetworks/field-signup-form/dist/index.css'
import '@piconetworks/field-email-password-login/dist/index.css'
import '@piconetworks/field-markdown/dist/index.css'
import '@piconetworks/field-newsletters/dist/index.css'
import '@piconetworks/field-payment/dist/index.css'
import '@piconetworks/field-select-donation/dist/index.css'
import '@piconetworks/field-select-pass/dist/index.css'
import '@piconetworks/field-select-pledge/dist/index.css'
import '@piconetworks/field-select-subscription/dist/index.css'
import '@piconetworks/field-select-trial/dist/index.css'
import '@piconetworks/field-social-login/dist/index.css'

import Address from '@piconetworks/field-address'
import Date from '@piconetworks/field-date'
import Dropdown from '@piconetworks/field-dropdown'
import EmailPasswordLogin from '@piconetworks/field-email-password-login'

import GiftForm from '@piconetworks/field-gift-form'
import Markdown from '@piconetworks/field-markdown'
import Newsletters from '@piconetworks/field-newsletters'
import Number from '@piconetworks/field-number'
import SelectDonation from '@piconetworks/field-select-donation'

import SelectPass from '@piconetworks/field-select-pass'
import SelectPledge from '@piconetworks/field-select-pledge'
import SelectSubscription from '@piconetworks/field-select-subscription'
import SelectTrial from '@piconetworks/field-select-trial'

import SignupForm from '@piconetworks/field-signup-form'
import SocialLogin from '@piconetworks/field-social-login'
import Text from '@piconetworks/field-text'
import Payment from '@piconetworks/field-payment'

import {
    DonationExistingSwitch,
    DonationNewOneTime,
    DonationNewRecurring,
    GiftExistingConvert,
    GiftNewPledge,
    GiftNewRegular,
    PassNewRegular,
    SubscriptionExistingSwitch,
    SubscriptionNewPledge,
    SubscriptionNewRegular,
    // } from 'common/picore/src/packages/OrderSummary'
} from '@piconetworks/order-summary'

import moment from 'moment'

// compares fields array and form values
export const isCustomFieldsValid = (fields = [], values = {}) => {
    const requiredFields = fields.filter((field) => field?.required)

    const isValid = requiredFields.every(({ id }) => {
        const value = values[id]

        let isValueValid = false

        // address requires country / address line 1 / state|province|reigon / city / zip
        if (id === 'address') {
            isValueValid = !!(value?.address1
                && value?.city
                && value?.country
                && (value?.state || value?.province || value?.region)
                && value?.post_code)
        } else if (id === 'birthday') {
            isValueValid = !!value
        } else if (typeof value === 'object') {
            isValueValid = Object.keys(value).length
        } else {
            isValueValid = typeof value === 'number'
                ? value !== null
                : !!value
        }

        return isValueValid
    })
    return isValid
}

// add in :
// dynamic registry pattern
// service discovery e.g. EmailPasswordLogin() = typeMap.EmailPasswordLogin
// namespacing
// locale text copy int'l

const NewslettersInjected = ({
    name,
    label,
    description,
    required,
    defaultValue,
    linkColor,
    newsletterFilter,
    newsletters = [],
}) => (
    <Newsletters
        fieldContainerProps={{
            label,
            required,
        }}
        name={name}
        description={description}
        required={required}
        defaultValue={defaultValue}
        linkColor={linkColor}
        newsletterFilter={newsletterFilter}
        newsletters={newsletters}
    />
)

const giftCta = ({ popupId, dispatch }) => (optionId) => xFrame.launcher.openPopUp({
    dispatch,
    gift: true,
    optionId,
    popupId,
})

const typeMap = {
    Payment: ({
        existingPaymentMethods = [],
        changeCompletionStatus,
        isComplete,
        enableStripe = true,
        enablePlaid = false,
        sendExternalLink,
        linkColor,
        publisherName,
        defaultPaymentMethod = false,
        CardElement = () => null,
        deleteCard,
    }) => (
        <Payment
            changeCompletionStatus={changeCompletionStatus}
            isComplete={isComplete}
            enableStripe={enableStripe}
            enablePlaid={enablePlaid}
            sendExternalLink={sendExternalLink}
            existingPaymentMethods={existingPaymentMethods}
            defaultPaymentMethod={defaultPaymentMethod}
            linkColor={linkColor}
            publisherName={publisherName}
            CardElement={CardElement}
            deleteCard={deleteCard}
        />
    ),
    DonationExistingSwitch: ({
        state,
        option,
        product,
        customPrice,
        switchNow,
    }) => {
        const orderSummaryData = Loader.selectors.orderSummary(
            state,
            {
                optionId: option?.id,
                productId: product?.id,
                customPrice,
            },
        )
        const nextSubscriptionEnd = Loader.selectors.nextSubscriptionEnd(state)

        const productTitle = orderSummaryData?.productTitle
        const optionTypeAndInterval = orderSummaryData?.optionTypeAndInterval
        const renewalDate = switchNow?.switchNowStatus
            ? moment().format('LL')
            : moment(nextSubscriptionEnd).format('LL')
        const optionPrice = option?.price
            ? orderSummaryData?.optionPrice
            : orderSummaryData?.optionCustomPrice
        const optionInterval = orderSummaryData?.optionInterval
        const oldOptionPrice = orderSummaryData?.oldOptionPrice

        return (
            <DonationExistingSwitch
                productTitle={productTitle}
                optionTypeAndInterval={optionTypeAndInterval}
                renewalDate={renewalDate}
                optionPrice={optionPrice}
                optionInterval={optionInterval}
                switchNow={switchNow}
                oldOptionPrice={oldOptionPrice}
            />
        )
    },
    SubscriptionNewRegular: ({
        state,
        option,
        product,
    }) => {
        const orderSummaryData = Loader.selectors.orderSummary(
            state,
            {
                optionId: option?.id,
                productId: product?.id,
            },
        )

        const productTitle = orderSummaryData?.productTitle
        const optionTypeAndInterval = orderSummaryData?.optionTypeAndInterval
        const renewalDate = orderSummaryData?.renewalDate
        const optionPrice = orderSummaryData?.optionPrice
        const optionInterval = orderSummaryData?.optionInterval

        return (
            <SubscriptionNewRegular
                productTitle={productTitle}
                optionTypeAndInterval={optionTypeAndInterval}
                renewalDate={renewalDate}
                optionPrice={optionPrice}
                optionInterval={optionInterval}
            />
        )
    },
    SubscriptionNewPledge: ({
        state,
        option,
        product,
        customPrice,
    }) => {
        const orderSummaryData = Loader.selectors.orderSummary(
            state,
            {
                optionId: option?.id,
                productId: product?.id,
                customPrice,
            },
        )

        const productTitle = orderSummaryData?.productTitle
        const optionTypeAndInterval = orderSummaryData?.optionTypeAndInterval
        const renewalDate = orderSummaryData?.renewalDate
        const optionPrice = orderSummaryData?.optionCustomPrice || orderSummaryData?.optionPrice
        const optionInterval = orderSummaryData?.optionInterval
        const trialPrice = orderSummaryData?.trialPrice
        const trialDuration = orderSummaryData?.trialDuration
        const trialFinishDate = orderSummaryData?.trialFinishDate
        const isFree = orderSummaryData?.isFree

        return (
            <SubscriptionNewPledge
                productTitle={productTitle}
                optionTypeAndInterval={optionTypeAndInterval}
                renewalDate={renewalDate}
                optionPrice={optionPrice}
                optionInterval={optionInterval}
                trialPrice={trialPrice}
                trialDuration={trialDuration}
                trialFinishDate={trialFinishDate}
                isFree={isFree}
            />
        )
    },
    SubscriptionExistingSwitch: ({
        state,
        option,
        product,
        switchNow,
        customPrice,
    }) => {
        const orderSummaryData = Loader.selectors.orderSummary(
            state,
            {
                optionId: option?.id,
                productId: product?.id,
                customPrice,
            },
        )
        const nextSubscriptionEnd = Loader.selectors.nextSubscriptionEnd(state)

        const productTitle = orderSummaryData?.productTitle
        const optionTypeAndInterval = orderSummaryData?.optionTypeAndInterval

        const renewalDate = switchNow?.switchNowStatus
            ? moment().format('LL')
            : moment(nextSubscriptionEnd).format('LL')
        const optionPrice = orderSummaryData?.optionCustomPrice || orderSummaryData?.optionPrice
        const optionInterval = orderSummaryData?.optionInterval
        const oldOptionPrice = orderSummaryData?.oldOptionPrice

        return (
            <SubscriptionExistingSwitch
                productTitle={productTitle}
                optionTypeAndInterval={optionTypeAndInterval}
                renewalDate={renewalDate}
                optionPrice={optionPrice}
                optionInterval={optionInterval}
                oldOptionPrice={oldOptionPrice}
                switchNow={switchNow}
            />
        )
    },
    GiftNewRegular: ({
        state,
        option,
        product,
    }) => {
        const orderSummaryData = Loader.selectors.orderSummary(
            state,
            {
                optionId: option?.id,
                productId: product?.id,
            },
        )

        const productTitle = orderSummaryData?.productTitle
        const optionTypeAndInterval = orderSummaryData?.optionTypeAndInterval
        const optionPrice = orderSummaryData?.optionPrice
        const optionInterval = orderSummaryData?.optionInterval

        return (
            <GiftNewRegular
                productTitle={productTitle}
                optionTypeAndInterval={optionTypeAndInterval}
                optionPrice={optionPrice}
                optionInterval={optionInterval}
            />
        )
    },
    GiftNewPledge: ({
        state,
        option,
        product,
        customPrice,
    }) => {
        const orderSummaryData = Loader.selectors.orderSummary(
            state,
            {
                optionId: option?.id,
                productId: product?.id,
                customPrice,
            },
        )

        const productTitle = orderSummaryData?.productTitle
        const optionTypeAndInterval = orderSummaryData?.optionTypeAndInterval
        const optionPrice = orderSummaryData?.optionCustomPrice || orderSummaryData?.optionPrice
        const optionInterval = orderSummaryData?.optionInterval

        return (
            <GiftNewPledge
                productTitle={productTitle}
                optionTypeAndInterval={optionTypeAndInterval}
                optionPrice={optionPrice}
                optionInterval={optionInterval}
            />
        )
    },
    GiftExistingConvert: ({
        state,
        option,
        product,
    }) => {
        const orderSummaryData = Loader.selectors.orderSummary(
            state,
            {
                optionId: option?.id,
                productId: product?.id,
            },
        )

        const productTitle = orderSummaryData?.productTitle
        const optionTypeAndInterval = orderSummaryData?.optionTypeAndInterval
        const renewalDate = orderSummaryData?.renewalDate
        const optionPrice = orderSummaryData?.optionPrice
        const optionInterval = orderSummaryData?.optionInterval

        return (
            <GiftExistingConvert
                productTitle={productTitle}
                optionTypeAndInterval={optionTypeAndInterval}
                renewalDate={renewalDate}
                optionPrice={optionPrice}
                optionInterval={optionInterval}
            />
        )
    },
    DonationNewRecurring: ({
        state,
        option,
        product,
        customPrice,
    }) => {
        const orderSummaryData = Loader.selectors.orderSummary(
            state,
            {
                optionId: option?.id,
                productId: product?.id,
                customPrice,
            },
        )

        const productTitle = orderSummaryData?.productTitle
        const optionTypeAndInterval = orderSummaryData?.optionTypeAndInterval
        const renewalDate = orderSummaryData?.renewalDate
        const optionPrice = orderSummaryData?.optionCustomPrice || orderSummaryData?.optionPrice
        const optionInterval = orderSummaryData?.optionInterval

        return (
            <DonationNewRecurring
                productTitle={productTitle}
                optionTypeAndInterval={optionTypeAndInterval}
                renewalDate={renewalDate}
                optionPrice={optionPrice}
                optionInterval={optionInterval}
            />
        )
    },
    DonationNewOneTime: ({
        state,
        option,
        product,
        customPrice,
    }) => {
        const orderSummaryData = Loader.selectors.orderSummary(
            state,
            {
                optionId: option?.id,
                productId: product?.id,
                customPrice,
            },
        )

        const productTitle = orderSummaryData?.productTitle
        const optionPrice = orderSummaryData?.optionCustomPrice || orderSummaryData?.optionPrice

        return (
            <DonationNewOneTime
                productTitle={productTitle}
                optionPrice={optionPrice}
            />
        )
    },
    PassNewRegular: ({
        state,
        option,
        product,
    }) => {
        const orderSummaryData = Loader.selectors.orderSummary(
            state,
            {
                optionId: option?.id,
                productId: product?.id,
            },
        )

        const productTitle = orderSummaryData?.productTitle
        const optionPrice = orderSummaryData?.optionPrice
        const passDuration = orderSummaryData?.passDuration

        return (
            <PassNewRegular
                productTitle={productTitle}
                optionPrice={optionPrice}
                passDuration={passDuration}
            />
        )
    },
    GiftForm: ({
        name,
        label,
        description,
        required,
        defaultValue,
    }) => (
        <GiftForm
            fieldContainerProps={{
                label,
                required,
            }}
            name={name}
            description={description}
            required={required}
            defaultValue={defaultValue}
        />
    ),
    Markdown: ({
        name,
        label,
        description,
        required,
        defaultValue,
        url,
        onClickLink,
        options,
    }) => {
        const linkColor = useSelector(Loader.selectors.linkColor)

        return (
            <Markdown
                fieldContainerProps={{
                    label,
                    required,
                }}
                name={name}
                linkColor={linkColor}
                description={description}
                required={required}
                defaultValue={defaultValue}
                url={url}
                onClickLink={onClickLink}
                options={options}
            />
        )
    },
    EmailPasswordLogin: ({
        customProperties,
        newsletters,
        name,
        label,
        description,
        required,
        defaultValue,
        disabled,
        customFieldsValid,
        router,
        loginEmail,
        popupId,
        checkoutSelection,
    }) => {
        const utm = useSelector(Zoid.selectors.getUTM)
        const redirectUrl = useSelector(Zoid.selectors.url)

        const linkColor = useSelector(Loader.selectors.linkColor)
        const contrastColor = useSelector(Loader.selectors.contrastColor)
        const publisherId = useSelector(Loader.selectors.publisherId)
        const userHasPassword = useSelector((state) => Account.selectors.hasPassword(state))
        const origin = useSelector(Zoid.selectors.origin)

        const dispatch = useDispatch()

        return (
            <EmailPasswordLogin
                fieldContainerProps={{
                    label,
                    required,
                }}
                name={name}
                customFieldsValid={customFieldsValid}
                description={description}
                required={required}
                defaultValue={defaultValue}
                linkColor={linkColor}
                contrastColor={contrastColor}
                disabled={disabled}
                userHasPassword={userHasPassword}
                loginEmail={loginEmail}
                checkEmail={async ({
                    email,
                }) => {
                    if (!email) {
                        throw new Error('no email provided')
                    }
                    const getHasPassword = await observers.getHasPassword({
                        dispatch,
                        payload: {
                            email,
                            publisherId,
                        },
                    })

                    if (getHasPassword?.hasPassword === false) {
                        const requestLoginLink = await observers.requestLoginLink({
                            dispatch,
                            payload: {
                                customProperties,
                                newsletters,
                                email,
                                publisherId,
                                redirectUrl: !checkoutSelection ? redirectUrl : `${redirectUrl}${redirectUrl.includes('?') ? '&' : '?'}${qs.stringify({ ...checkoutSelection, pn: 'resume_checkout' }, { skipNulls: true })}`,
                                utm,
                                popupId,
                                origin,
                            },
                        })

                        if (requestLoginLink?.isNewRegistration) {
                            return {
                                loggedIn: true,
                            }
                        }

                        const { query } = router

                        router.push({
                            pathname: '/login/link-confirmation',
                            query,
                        }, {
                            pathname: '/login/link-confirmation',
                            query,
                        })

                        return {
                            magicLink: true,

                        }
                    }

                    if (getHasPassword?.hasPassword === true) {
                        dispatch(Account.creators.setLoginEmail({
                            email,
                        }))

                        return {
                            email,
                            hasPassword: true,
                        }
                    }

                    return {}
                }}
                checkEmailPassword={async ({
                    email,
                    password,
                }) => {
                    await observers.login({
                        dispatch,
                        payload: {
                            email,
                            password,
                            publisherId,
                        },
                    })

                    return {}
                }}
            />
        )
    },
    SocialLogin: ({
        name,
        label,
        description,
        required,
        defaultValue,
        disabled,
        popupId,
        customProperties,
        newsletters,
        authenticationMethods,
    }) => {
        const dispatch = useDispatch()

        const publisherId = useSelector((state) => Loader.selectors.publisherId(state))
        const userId = useSelector((state) => state?.loader?.guest?.user?.id)
        const access_token = useSelector((state) => state?.loader?.guest?.token)
        const hasUserLoader = !!useSelector((state) => state.loader.user)

        const articleId = useSelector(Loader.selectors.articleId)
        const utm = useSelector(Zoid.selectors.getUTM)
        const origin = useSelector(Zoid.selectors.origin)
        const referer = useSelector(Zoid.selectors.referer)

        return (
            <SocialLogin
                authenticationMethods={authenticationMethods}
                fieldContainerProps={{
                    label,
                    required,
                }}
                origin={origin}
                referer={referer}
                params={{
                    utm,
                    article_id: articleId, // eslint-disable-line camelcase
                    form_id: popupId, // eslint-disable-line camelcase
                    custom_properties: customProperties, // eslint-disable-line camelcase
                    newsletter_ids: newsletters, // eslint-disable-line camelcase
                    calling_origin: `https://${process.env.HOSTNAME}/`,
                }}
                name={name}
                description={description}
                required={required}
                defaultValue={defaultValue}
                onLogin={(response) => {
                    dispatch(Auth.creators.socialLogin({
                        accessToken: response.token,
                        refreshToken: response.refresh_token,
                    }))
                }}
                publisherId={publisherId}
                userId={userId}
                access_token={access_token}
                isLoggedIn={hasUserLoader}
                disabled={disabled}
            />
        )
    },
    SignupForm: ({
        name,
        label,
        description,
        required,
        defaultValue,
    }) => {
        const linkColor = useSelector(Loader.selectors.linkColor)

        return (
            <SignupForm
                fieldContainerProps={{
                    label,
                    required,
                }}
                name={name}
                description={description}
                required={required}
                defaultValue={defaultValue}
                linkColor={linkColor}
            />
        )
    },
    SelectDonation: ({
        name,
        required,
        product,
        isGift,
        setProductOptionId,
        setCustomPrice,
        currentPurchaseOptionId,
    }) => {
        const linkColor = useSelector(Loader.selectors.linkColor)
        const contrastColor = useSelector(Loader.selectors.contrastColor)
        const isGroupMember = useSelector(Loader.selectors.isGroupMember)

        const customFeaturesUrl = product?.custom_text?.file_name

        return (
            <SelectDonation
                name={name}
                required={required}
                product={product}
                linkColor={linkColor}
                contrastColor={contrastColor}
                isGift={isGift}
                setProductOptionId={setProductOptionId}
                setCustomPrice={setCustomPrice}
                currentPurchaseOptionId={currentPurchaseOptionId}
                customFeaturesUrl={customFeaturesUrl}
                isGroupMember={isGroupMember}
            />
        )
    },
    SelectPass: ({
        name,
        required,
        product,
        isGift,
    }) => {
        // eslint-disable-next-line max-len
        const popupId = useSelector((state) => Loader.selectors.popupGetPopupByProductId(state, product?.id))?.id
        const linkColor = useSelector(Loader.selectors.linkColor)
        const contrastColor = useSelector(Loader.selectors.contrastColor)

        const customFeaturesUrl = product?.custom_text?.file_name

        const dispatch = useDispatch()

        return (
            <SelectPass
                name={name}
                required={required}
                product={product}
                linkColor={linkColor}
                contrastColor={contrastColor}
                isGift={isGift}
                giftCta={giftCta({ popupId, dispatch })}
                customFeaturesUrl={customFeaturesUrl}
            />
        )
    },
    SelectSubscription: ({
        name,
        required,
        product,
        isGift,
    }) => {
        // eslint-disable-next-line max-len
        const popupId = useSelector((state) => Loader.selectors.popupGetPopupByProductId(state, product?.id))?.id
        const linkColor = useSelector(Loader.selectors.linkColor)
        const contrastColor = useSelector(Loader.selectors.contrastColor)

        const customFeaturesUrl = product?.custom_text?.file_name

        const dispatch = useDispatch()
        return (
            <SelectSubscription
                name={name}
                required={required}
                product={product}
                linkColor={linkColor}
                contrastColor={contrastColor}
                isGift={isGift}
                giftCta={giftCta({ popupId, dispatch })}
                customFeaturesUrl={customFeaturesUrl}
            />
        )
    },
    SelectPledge: ({
        name,
        required,
        product,
        isGift,
        setCustomPrice,
    }) => {
        // eslint-disable-next-line max-len
        const popupId = useSelector((state) => Loader.selectors.popupGetPopupByProductId(state, product?.id))?.id
        const linkColor = useSelector(Loader.selectors.linkColor)
        const contrastColor = useSelector(Loader.selectors.contrastColor)

        const customFeaturesUrl = product?.custom_text?.file_name

        const dispatch = useDispatch()

        return (
            <SelectPledge
                name={name}
                required={required}
                product={product}
                linkColor={linkColor}
                contrastColor={contrastColor}
                isGift={isGift}
                giftCta={giftCta({ popupId, dispatch })}
                customFeaturesUrl={customFeaturesUrl}
                setCustomPrice={setCustomPrice}
            />
        )
    },
    SelectTrial: ({
        name,
        required,
        product,
        isGift,
    }) => {
        // eslint-disable-next-line max-len
        const popupId = useSelector((state) => Loader.selectors.popupGetPopupByProductId(state, product?.id))?.id
        const linkColor = useSelector(Loader.selectors.linkColor)
        const contrastColor = useSelector(Loader.selectors.contrastColor)

        const customFeaturesUrl = product?.custom_text?.file_name

        const dispatch = useDispatch()

        return (
            <SelectTrial
                name={name}
                required={required}
                product={product}
                linkColor={linkColor}
                contrastColor={contrastColor}
                isGift={isGift}
                giftCta={giftCta({ popupId, dispatch })}
                customFeaturesUrl={customFeaturesUrl}
            />
        )
    },
    Date: ({
        name,
        label,
        description,
        required,
    }) => (
        <Date
            fieldContainerProps={{
                label,
                required,
            }}
            name={name}
            description={description}
            required={required}
        />
    ),
    Dropdown: ({
        name,
        label,
        description,
        required,
        defaultValue,
        options,
    }) => {
        const contrastColor = useSelector(Loader.selectors.contrastColor)
        const themeColor = useSelector(Loader.selectors.linkColor)

        return (
            <Dropdown
                fieldContainerProps={{
                    label,
                    required,
                }}
                name={name}
                description={description}
                required={required}
                defaultValue={defaultValue}
                options={options}
                contrastColor={contrastColor}
                themeColor={themeColor}
            />
        )
    },
    Address: ({
        name,
        label,
        description,
        required,
        defaultValue,
    }) => {
        const linkColor = useSelector(Loader.selectors.linkColor)

        return (
            <Address
                fieldContainerProps={{
                    label,
                    required,
                }}
                name={name}
                description={description}
                required={required}
                defaultValue={defaultValue}
                themeColor={linkColor}
            />
        )
    },
    Text: ({
        name,
        label,
        description,
        required,
        defaultValue,
        errorMessage,
    }) => {
        const linkColor = useSelector(Loader.selectors.linkColor)

        return (
            <Text
                fieldContainerProps={{
                    label,
                    required,
                }}
                name={name}
                description={description}
                required={required}
                defaultValue={defaultValue}
                themeColor={linkColor}
                showErrorState={!!errorMessage}
            />
        )
    },
    Number: ({
        name,
        label,
        description,
        required,
        defaultValue,
    }) => {
        const linkColor = useSelector(Loader.selectors.linkColor)

        return (
            <Number
                fieldContainerProps={{
                    label,
                    required,
                }}
                name={name}
                description={description}
                required={required}
                defaultValue={defaultValue}
                themeColor={linkColor}
            />
        )
    },
    Newsletters: NewslettersInjected,
}

const getFields = (fields) => (
    fields
        .filter(({
            type,
            id,
        }) => (
            !!type
            && !!typeMap[type]
            && !!id
        ))
        .sort(({ order: a }, { order: b }) => a - b)
        .map(({
            type,
            id,
            title,
            ...rest
        }) => {
            const Field = typeMap[type]

            return (props) => (
                <Field
                    {...{
                        name: id,
                        label: title,
                    }}
                    {...rest}
                    {...props}
                />
            )
        })
)

export default getFields

export {
    NewslettersInjected as Newsletters,
}
